import { useAppSelector } from '@/shared';
import {
	RubberOperationResponse,
	RubberOperationUpdateRequest,
	RubberTechMapResponse,
	useUpdateRubberTechMapMutation,
} from '@/shared/state/api/swagger';
import { sortByOperations, sortOperations } from '../utils/map-data';

export const useRubberOperationFillForm = () => {
	const [updateRubberTechMap] = useUpdateRubberTechMapMutation();

	const userRole = useAppSelector((state) => state.auth.userProfile?.role);
	const isStorekeeperRole = userRole === 'storekeeper';

	const handleCurrentOperation = (data: RubberOperationUpdateRequest, techMap: RubberTechMapResponse) => {
		return new Promise((resolve, reject) => {
			try {
				const { id } = techMap.current_rubber_operation as RubberOperationResponse;
				const { rubber_operations } = techMap;
				const default_rubber_operations = rubber_operations?.filter((operation) => operation.is_default);
				const operations = rubber_operations?.filter((operation) => !operation.is_default);
				if (!id || !default_rubber_operations || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: RubberOperationUpdateRequest = { ...data, id };

				// объединяем все операции (кастомные и дефолтные) для единого обновления
				const allOperations = [...operations, ...default_rubber_operations];

				// вставляем объект операции на место измененного
				const updatedAllOperations = allOperations.map((op) => (op.id === id ? { ...op, ...payload } : op));

				const { operationsWithCopyFields } = getAllRubberOperationsWithCopyFields(
					payload,
					updatedAllOperations,
					default_rubber_operations,
				);

				// фильтруем только операции, которые нужно обновить (существующие с id)
				const operationsToUpdate = operationsWithCopyFields
					.filter((op) => op.id)
					.map((op) => ({
						...op,
						id: op.id!,
					}));

				// Кладовщик может обновлять только дефолтные операции
				// И последнюю кастомную через чейн-логику
				let operationsToSend = operationsToUpdate;
				if (isStorekeeperRole) {
					const defaultOps = operationsToUpdate.filter((op) => op.is_default === true);

					const customOperations = updatedAllOperations.filter((op) => !op.is_default);
					const lastCustomIndex = customOperations.length - 1;
					const updatedLastCustom = operationsToUpdate.find(
						(op) => !op.is_default && op.step_id === customOperations[lastCustomIndex]?.step_id,
					);
					const originalLastCustom = customOperations[lastCustomIndex];

					const isChainUpdated =
						updatedLastCustom &&
						originalLastCustom &&
						updatedLastCustom.count_in_number !== originalLastCustom.count_in_number;

					operationsToSend = isChainUpdated ? [...defaultOps, updatedLastCustom] : defaultOps;
				}

				updateRubberTechMap({
					rubberTechMapId: techMap.id!,
					rubberTechMapUpdateRequest: {
						rubber_operations: {
							rubber_operations_to_update: operationsToSend,
							rubber_operations_to_create: [], // Only updating existing operations
						},
					},
				})
					.unwrap()
					.then((res) => {
						resolve(res);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update rubber operation error:', error);
				reject(error);
			}
		});
	};

	const handleNotCurrentOperation = (data: RubberOperationUpdateRequest, techMap: RubberTechMapResponse) => {
		return new Promise<RubberOperationResponse[]>((resolve, reject) => {
			try {
				const allOperations = sortOperations(techMap);

				// достаем значения предыдущего состояния измененного объекта операции
				const { id } = allOperations.find((op) => op.step_id === data.step_id) as RubberOperationResponse;

				const { rubber_operations } = techMap;
				const default_rubber_operations = rubber_operations?.filter((operation) => operation.is_default);
				const operations = rubber_operations?.filter((operation) => !operation.is_default);
				if (!id || !default_rubber_operations || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: RubberOperationUpdateRequest = { ...data, id };

				// объединяем все операции (кастомные и дефолтные) для единого обновления
				const allOperationsArray = [...operations, ...default_rubber_operations];

				// вставляем объект операции на место измененного
				const updatedAllOperations = allOperationsArray.map((op) =>
					op.id === id ? { ...op, ...payload } : op,
				);

				const { operationsWithCopyFields } = getAllRubberOperationsWithCopyFields(
					payload,
					updatedAllOperations,
					default_rubber_operations,
				);

				const currentOperationIdx = operationsWithCopyFields.findIndex(
					(op) => op.step_id === techMap.current_rubber_operation?.step_id,
				);
				const selectedOperationIdx = operationsWithCopyFields.findIndex((op) => op.step_id === data.step_id);
				const skippedOperations = operationsWithCopyFields.slice(currentOperationIdx, selectedOperationIdx);

				// фильтруем только операции, которые нужно обновить (существующие с id)
				const operationsToUpdate = operationsWithCopyFields
					.filter((op) => op.id)
					.map((op) => ({
						...op,
						id: op.id!,
					}));

				updateRubberTechMap({
					rubberTechMapId: techMap.id!,
					rubberTechMapUpdateRequest: {
						rubber_operations: {
							rubber_operations_to_update: operationsToUpdate,
							rubber_operations_to_create: [], // Only updating existing operations
						},
					},
				})
					.unwrap()
					.then(() => {
						resolve(skippedOperations as RubberOperationResponse[]);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update rubber operation error: ', error);
				reject(error);
			}
		});
	};

	return { handleCurrentOperation, handleNotCurrentOperation };
};

const getAllRubberOperationsWithCopyFields = (
	payload: RubberOperationUpdateRequest,
	allOperations: RubberOperationResponse[],
	defaultOperationsPayload: RubberOperationResponse[],
) => {
	const customOperations = allOperations.filter((op) => !op.is_default);
	const operations = sortByOperations(customOperations);
	const defaultOperations = sortByOperations(defaultOperationsPayload);

	let operationsWithCopyFields: RubberOperationResponse[] = operations;
	let defaultOperationsWithCopyFields: RubberOperationResponse[] = defaultOperations;

	const filledCustomOperationIndex = operations.findIndex((op) => op.step_id === payload.step_id);
	const isDefaultOperation = defaultOperations.some((op) => op.step_id === payload.step_id);
	const filledDefaultOperationIndex = defaultOperations.findIndex((op) => op.step_id === payload.step_id);
	const filledOperationIndex = sortByOperations(allOperations).findIndex((op) => op.step_id === payload.step_id);

	if (filledOperationIndex === -1) {
		return { operationsWithCopyFields: sortByOperations(allOperations) };
	}

	if (isDefaultOperation) {
		defaultOperationsWithCopyFields = defaultOperations.map((op) =>
			op.step_id === payload.step_id
				? {
						...op,
						count_in_number: payload.count_in_number,
						count_out_number: payload.count_out_number,
						wasted: payload.wasted,
						fio: payload.fio,
						date: payload.date,
				  }
				: op,
		);
	}

	if (filledCustomOperationIndex >= 0) {
		if (filledCustomOperationIndex === operations.length - 2) {
			defaultOperationsWithCopyFields = defaultOperationsWithCopyFields.map((op, index) => {
				return index === 0 && !op.is_canceled
					? {
							...op,
							count_in_number: payload.count_out_number,
					  }
					: op;
			});
		} else if (filledCustomOperationIndex === operations.length - 1) {
			defaultOperationsWithCopyFields = defaultOperationsWithCopyFields.map((op, index) => {
				return index === 1 && !op.is_canceled
					? {
							...op,
							count_in_number: payload.count_out_number,
					  }
					: op;
			});
		} else {
			operationsWithCopyFields = operations.reduce(
				(acc: { result: RubberOperationResponse[]; replaced: boolean }, op, index) => {
					if (index <= filledCustomOperationIndex) {
						acc.result.push(op);
					} else {
						if (!acc.replaced && !op.is_canceled) {
							acc.result.push({
								...op,
								count_in_number: payload.count_out_number ?? undefined,
							});
							acc.replaced = true;
						} else {
							acc.result.push(op);
						}
					}
					return acc;
				},
				{ result: [], replaced: false },
			).result;
		}
	} else if (isDefaultOperation) {
		if (filledDefaultOperationIndex === 0) {
			operationsWithCopyFields = operations.map((op, index) => {
				return index === operations.length - 1 && !op.is_canceled
					? {
							...op,
							count_in_number: payload.count_out_number,
					  }
					: op;
			});
		}
	}

	return { operationsWithCopyFields: [...operationsWithCopyFields, ...defaultOperationsWithCopyFields] };
};
